/**
 * C7.3 — first-party telemetry. Captures boot/gameplay events and crash
 * reports on the client and beacons them to the Cloudflare Pages Function at
 * `/api/telemetry`, which persists them to D1.
 *
 * Deliberately free of DOM and Three, and with its storage + transport
 * injectable, so `tools/probe-telemetry.mjs` can drive it in Node. The default
 * singleton (`telemetry`) uses `localStorage` + `fetch` and is what `main.ts`
 * and `Game.ts` talk to.
 *
 * Design notes:
 * - Never blocks the game. `track` is synchronous and cheap; the network send
 *   happens on a timer or on unload, never on the frame path.
 * - Never loses a crash report. Events are persisted to storage as they arrive
 *   and only removed after the transport confirms a successful POST; a failed
 *   POST (endpoint down, offline) re-queues the batch for a later retry.
 * - Privacy: no raw name, no full user agent. Context carries the identity
 *   voice + name length, a `mobile` flag and a viewport string only.
 */

export type TelemetryEventType =
  | 'boot_success'
  | 'boot_failed'
  | 'run_start'
  | 'run_end'
  | 'scan'
  | 'part_collected'
  | 'part_installed'
  | 'repair_done'
  | 'escape'
  | 'error'
  | 'context_lost';

export interface TelemetryEvent {
  readonly type: TelemetryEventType;
  /** Epoch ms when the event happened. */
  readonly ts: number;
  readonly session: string;
  readonly payload?: Readonly<Record<string, unknown>>;
}

export interface TelemetryContext {
  readonly tier?: string;
  readonly backend?: string;
  readonly mobile?: boolean;
  readonly voice?: string;
  readonly nameLength?: number;
  readonly viewport?: string;
}

interface TelemetryStorage {
  get(key: string): string | null;
  set(key: string, value: string): void;
}

interface TelemetryTransport {
  /** Send a JSON batch to the endpoint; resolve `true` only on a confirmed success. */
  send(url: string, body: string): Promise<boolean>;
}

export interface TelemetryOptions {
  /** Absolute or relative endpoint. Default `/api/telemetry`. */
  endpoint?: string;
  /** Injectable storage for tests; defaults to `localStorage`. */
  storage?: TelemetryStorage;
  /** Injectable transport for tests; defaults to `fetch` + `keepalive`. */
  transport?: TelemetryTransport;
  /** Max events held before the oldest are dropped. Default 200. */
  maxQueue?: number;
  /** How often to attempt a flush while events are pending. Default 5 s. */
  flushIntervalMs?: number;
  /** Whether to start the flush timer. Default true. */
  autoFlush?: boolean;
  /** Start enabled. When disabled, `track`/`flush` no-op. Default true. */
  enabled?: boolean;
}

const DEFAULT_STORAGE_KEY = 'extinctrum.telemetry';
const STORAGE_SESSION_KEY = `${DEFAULT_STORAGE_KEY}.session`;
const STORAGE_QUEUE_KEY = `${DEFAULT_STORAGE_KEY}.queue`;
const DEFAULT_ENDPOINT = '/api/telemetry';
const DEFAULT_MAX_QUEUE = 200;
const DEFAULT_FLUSH_INTERVAL = 5000;

function randomId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function defaultStorage(): TelemetryStorage {
  return {
    get: (key) => {
      try {
        return localStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set: (key, value) => {
      try {
        localStorage.setItem(key, value);
      } catch {
        /* private mode — keep events in memory only */
      }
    },
  };
}

function defaultTransport(): TelemetryTransport {
  return {
    async send(url, body) {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      });
      if (!res.ok) return false;
      // The Vite dev server answers `/api/*` with the SPA fallback (200 HTML).
      // Treat a non-JSON body as a failure so events are retried, not dropped.
      const type = res.headers.get('content-type') ?? '';
      return type.includes('application/json');
    },
  };
}

export class Telemetry {
  private readonly endpoint: string;
  private readonly storage: TelemetryStorage;
  private readonly transport: TelemetryTransport;
  private readonly maxQueue: number;
  private readonly flushIntervalMs: number;

  private enabled: boolean;
  private queue: TelemetryEvent[] = [];
  private context: TelemetryContext = {};
  private session = '';
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private flushing = false;

  constructor(opts: TelemetryOptions = {}) {
    this.endpoint = opts.endpoint ?? DEFAULT_ENDPOINT;
    this.storage = opts.storage ?? defaultStorage();
    this.transport = opts.transport ?? defaultTransport();
    this.maxQueue = opts.maxQueue ?? DEFAULT_MAX_QUEUE;
    this.flushIntervalMs = opts.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL;
    this.enabled = opts.enabled ?? true;
    this.session = this.restoreSession();
    this.restoreQueue();
    if (this.enabled && (opts.autoFlush ?? true)) this.startFlushTimer();
  }

  /** Record an event. Never throws. */
  track(type: TelemetryEventType, payload?: Readonly<Record<string, unknown>>): void {
    if (!this.enabled) return;
    const event: TelemetryEvent = {
      type,
      ts: Date.now(),
      session: this.session,
      ...(payload ? { payload } : {}),
    };
    this.queue.push(event);
    this.trim();
    this.persistQueue();
    this.scheduleFlush();
  }

  setContext(ctx: TelemetryContext): void {
    this.context = { ...this.context, ...ctx };
  }

  /** Disable recording + flushing. Idempotent; used to silence dev/harness runs. */
  disable(): void {
    this.enabled = false;
    this.stopFlushTimer();
  }

  /** Enable recording again (mostly for tests). */
  enable(): void {
    this.enabled = true;
    this.startFlushTimer();
  }

  /** Send all pending events. Resolves true if the batch was accepted. */
  async flush(): Promise<boolean> {
    if (!this.enabled || this.flushing) return false;
    if (this.queue.length === 0) return true;
    this.flushing = true;
    const batch = this.queue.splice(0, this.queue.length);
    const body = JSON.stringify({ context: this.context, events: batch });
    let ok = false;
    try {
      ok = await this.transport.send(this.endpoint, body);
    } catch {
      ok = false;
    }
    if (!ok) {
      // Keep the batch for a later retry — oldest first, capped.
      this.queue = batch.concat(this.queue);
      this.trim();
    }
    this.persistQueue();
    this.flushing = false;
    return ok;
  }

  /**
   * Fire-and-forget unload flush via `sendBeacon`, which survives page teardown.
   * Called on `pagehide` / `visibilitychange→hidden` so a tab that dies after a
   * crash still reports the events captured up to that point.
   */
  flushBeacon(): void {
    if (!this.enabled || this.queue.length === 0) return;
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      void this.flush();
      return;
    }
    const batch = this.queue;
    this.queue = [];
    this.persistQueue();
    const body = JSON.stringify({ context: this.context, events: batch });
    try {
      navigator.sendBeacon(this.endpoint, new Blob([body], { type: 'application/json' }));
    } catch {
      this.queue = batch.concat(this.queue);
      this.trim();
      this.persistQueue();
    }
  }

  /** Install global crash capture (uncaught errors + unhandled rejections). */
  installCrashCapture(): void {
    if (typeof window === 'undefined') return;
    window.addEventListener('error', (e) => {
      this.track('error', {
        kind: 'uncaught',
        message: String(e.message).slice(0, 300),
        stack: stackOf(e.error).slice(0, 1200),
        source: e.filename ?? '',
        line: e.lineno ?? 0,
      });
    });
    window.addEventListener('unhandledrejection', (e) => {
      this.track('error', {
        kind: 'rejection',
        message: String(e.reason).slice(0, 300),
        stack: stackOf(e.reason).slice(0, 1200),
      });
    });
  }

  get pending(): number {
    return this.queue.length;
  }

  private restoreSession(): string {
    const existing = this.storage.get(STORAGE_SESSION_KEY);
    if (existing) return existing;
    const id = randomId();
    this.storage.set(STORAGE_SESSION_KEY, id);
    return id;
  }

  private restoreQueue(): void {
    const raw = this.storage.get(STORAGE_QUEUE_KEY);
    if (!raw) return;
    try {
      const parsed: unknown = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        this.queue = parsed
          .filter(isEvent)
          .slice(0, this.maxQueue);
      }
    } catch {
      this.queue = [];
    }
  }

  private persistQueue(): void {
    this.storage.set(STORAGE_QUEUE_KEY, JSON.stringify(this.queue));
  }

  private trim(): void {
    if (this.queue.length > this.maxQueue) {
      this.queue = this.queue.slice(this.queue.length - this.maxQueue);
    }
  }

  private scheduleFlush(): void {
    if (!this.enabled || this.flushTimer !== null) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, this.flushIntervalMs);
  }

  private startFlushTimer(): void {
    if (this.flushTimer !== null) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = null;
      void this.flush();
    }, this.flushIntervalMs);
  }

  private stopFlushTimer(): void {
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}

function isEvent(v: unknown): v is TelemetryEvent {
  if (typeof v !== 'object' || v === null) return false;
  const e = v as Record<string, unknown>;
  return (
    typeof e.type === 'string' &&
    typeof e.ts === 'number' &&
    typeof e.session === 'string'
  );
}

function stackOf(err: unknown): string {
  if (err instanceof Error && typeof err.stack === 'string') return err.stack;
  return String(err);
}

export const telemetry = new Telemetry();
